import './ProspectPage.css';
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguageContext } from "../../context/LanguageContext";
import supabase from "../../services/supabaseClient";
import {
  formatDate,
  convertHeightToMeters,
  convertPoundsToKilograms,
  translateExperienceToHeb
} from "../../utils";
import ScoutingNotes from "../../components/ScoutingNotes";
import Tooltip from '../../components/Tooltip';
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import { GiCrossbow, GiBlackHandShield } from "react-icons/gi";
import { ImStatsBars } from "react-icons/im";
import { MdOutlinePsychologyAlt, MdOutlineSportsHandball } from "react-icons/md";
import { FaWeightScale } from "react-icons/fa6";
import { GrNotes } from "react-icons/gr";
import { BsProjector } from "react-icons/bs";

const ProspectPage = () => {
  const { id } = useParams<{ id: string }>();
  const { isHE } = useLanguageContext();
  const [prospect, setProspect] = useState<any | null>(null);
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState({
    offenseStrengths: true,
    offenseWeaknesses: true,
    defenseStrengths: true,
    defenseWeaknesses: true,
  });

  const toggleCollapse = (section: keyof typeof isCollapsed) => {
    setIsCollapsed((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const fetchProspectDetails = async (prospectId: number) => {
    try {
      const { data, error } = await supabase.rpc("get_prospect_details", { prospect_id: prospectId });

      if (error) {
        console.error("Error fetching prospect details:", error.message);
        return null;
      } else {
        if (data && data.length > 0) {
          return data[0];
        } else {
          console.log("No prospect data found for ID:", prospectId);
          return null;
        }
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      return null;
    }
  };

  const fetchPhotoURL = async (fileName: string): Promise<string | null> => {
    try {
      const { data } = supabase
        .storage
        .from('prospect-pics')
        .getPublicUrl(fileName);
      return data?.publicUrl || null;
    } catch (err) {
      console.error("Unexpected error fetching photo URL:", err);
      return null;
    }
  };

  // const processProspectData = (prospectData: any) => {
  //   if (!prospectData) return;

  //   const general = { ...prospectData };
  //   delete general.id;
  //   const englishData: Record<string, any> = {};
  //   const hebrewData: Record<string, any> = {};

  //   Object.keys(prospectData).forEach((key) => {
  //     if (Array.isArray(prospectData[key])) {
  //       englishData[key] = filterByLanguage(prospectData[key], false);
  //       hebrewData[key] = filterByLanguage(prospectData[key], true);
  //       delete general[key];
  //     }
  //   });
  //   setGeneralData(general);
  // };

  useEffect(() => {
    document.body.classList.add('prospects-page');
    return () => {
      document.body.classList.remove('prospects-page');
    };
  }, []);

  useEffect(() => {
    const loadProspect = async () => {
      if (id) {
        const prospectData = await fetchProspectDetails(Number(id));
        if (prospectData) {
          setProspect(prospectData);
          const fileName = `${prospectData.full_name[0]}.jpg`;
          const photo = await fetchPhotoURL(fileName);
          if (photo) {
            setPhotoURL(photo);
          }
        }
      }
    };
    loadProspect();

  }, [id]);

  return (
    <div dir={isHE ? "rtl" : "ltr"}>
      {prospect ? (
        <div className="bg-terminal min-h-screen">
          <div className="container mx-auto px-6 py-8">
            <div
              className="bg-notebook-size bg-baseBeige opacity-95 min-h-screen relative rounded-lg shadow-md py-8 px-4"
              style={{
                backgroundImage: 'url("/assets/prospect-bg.png")',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >
              <div className='flex flex-col justify-between'>
                <div className="flex flex-col justify-between mb-4 md:flex-row">

                  <div
                    className="items-start flex flex-col"
                  >
                    <h2
                      className={
                        isHE ? "font-varelaRound text-xl font-bold" : "font-staatliches text-xl font-semibold"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <span>
                        {
                          isHE ?
                            prospect.full_name[1]
                            :
                            prospect.full_name[0]
                        },
                      </span>&nbsp;
                      <span>
                        {
                          isHE ?
                            prospect.pos_and_roles[1].position
                            :
                            prospect.pos_and_roles[0].position
                        }
                      </span>
                    </h2>
                    <div className="text-sm ">

                      <>
                        <p className={isHE ? " mb-1 text-right" : " mb-1 text-left"}>
                          {
                            isHE ?
                              prospect.team[1]
                              :
                              prospect.team[0]
                          }
                        </p>
                        <p className={isHE ? "mb-1 text-right" : "mb-1 text-left"}>
                          <span>
                            {isHE ? 'שנה:' : 'Years:'}
                          </span>&nbsp;
                          <span>
                            {
                              isHE ?
                                translateExperienceToHeb(prospect.experience_level) :
                                prospect.experience_level
                            }
                          </span>
                        </p>
                        <p className={isHE ? "mb-1 text-right" : "mb-1 text-left"}>
                          <span>
                            {isHE ? 'במקור מ:' : 'From:'}
                          </span>&nbsp;
                          <span className="mb-1">
                            {
                              isHE ?
                                prospect.place_of_birth[1]
                                :
                                prospect.place_of_birth[0]
                            }
                          </span>
                        </p>
                      </>
                    </div>
                    {
                      prospect.consenus_rank && (
                        <p className="mb-1 text-sm ">
                          <span>
                            {
                              isHE ? 'דירוג קונצנזואלי:' : 'Consensus rank:'
                            }
                          </span>&nbsp;
                          <span>
                            {prospect.consenus_rank}
                          </span>

                        </p>
                      )
                    }
                    <p className="mb-1 text-sm ">
                      <span>
                        {
                          isHE ? 'גיל בעת הדראפט:' : 'Draft age:'
                        }
                      </span>&nbsp;
                      <span>
                        {prospect.draft_age}
                      </span>
                    </p>
                    <p className="text-xs mt-2 mb-2">
                      <span>
                        {isHE ? 'עדכון אחרון:' : 'Last update:'}&nbsp;
                      </span>
                      <span>
                        {prospect.last_update}
                      </span>
                    </p>
                  </div>

                  <div className='mb-2 flex flex-col items-center'>
                    <ImStatsBars />
                    <a
                      href={prospect.stats_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-xl font-bold text-[#B8860B] hover:text-[#3E2F22] focus:text-[#3E2F22]"
                    >
                      {isHE ? "סטטיסטיקה אישית" : "Check My Stats"}
                    </a>
                  </div>

                  <div className="flex flex-col items-between">
                    <div className="text-xs size-32 self-center">
                      <Tooltip
                        content={prospect.pic_credit}
                        position={isHE ? 'right' : 'left'}
                      >
                        <img
                          src={photoURL || "../assets/headshot.jpg"}
                          alt={`${prospect.full_name[0]}`}
                          className="rounded-full mr-2 mb-1"
                        />
                      </Tooltip>
                    </div>
                  </div>
                </div>


              </div>

              <div className="mt-5">
                <div className={isHE ? "text-right" : "text-left"}>
                  <div className="mt-12 mb-5">
                    {
                      isHE ? prospect.summary[1] : prospect.summary[0]
                    }
                  </div>
                  <div className='grid grid-cols-1 gap-4 mt-5 md:grid-cols-2'>
                    <div>
                      <h3
                        className={
                          isHE ? "font-varelaRound text-xl font-bold" : "font-staatliches text-xl font-semibold"
                        }
                        style={{ color: "#7A4B4F" }}
                      >
                        <FaWeightScale />
                        {isHE ? 'נתונים פיזיים' : 'Physical Measurements'}
                      </h3>
                      {
                        prospect.phys_profile[0].date_measured && (
                          <p className="text-sm mb-2">
                            {isHE ? '(נכון ל- ' : '(as of '}
                            {formatDate(prospect.phys_profile[0].date_measured, isHE)}
                            {isHE ? ')' : ')'}
                          </p>
                        )
                      }
                      <ul className="list-none text-sm ">
                        <li className="flex mb-2">
                          <span>{isHE ? "גובה (בנעליים):" : "Height (w/shoes):"}</span>&nbsp;
                          <span dir="ltr">
                            {
                              isHE ?
                                convertHeightToMeters(prospect.phys_profile[0].height)
                                :
                                prospect.phys_profile[0].height

                            }
                          </span>
                        </li>
                        <li className="flex mb-2">
                          <span>{isHE ? 'משקל:' : 'Weight:'}</span>&nbsp;
                          <span>
                            {
                              isHE ?
                                convertPoundsToKilograms(prospect.phys_profile[0].weight)
                                :
                                prospect.phys_profile[0].weight
                            }
                          </span>
                        </li>
                        <li className="flex mb-2">
                          <span>{isHE ? 'מוטת זרועות:' : 'Wingspan:'}</span>&nbsp;
                          <span dir="ltr">
                            {
                              isHE ?
                                convertHeightToMeters(prospect.phys_profile[0].wingspan)
                                :
                                prospect.phys_profile[0].wingspan
                            }
                          </span>
                        </li>
                        <li className="flex mb-2">
                          <span>{isHE ? 'ניתור אנכי מקסימלי:' : 'Max Vertical:'}</span>&nbsp;
                          <span>
                            {
                              prospect.phys_profile[0].maxVertical === "N/A" ?
                                (
                                  isHE ?
                                    "נתון לא זמין" :
                                    prospect.phys_profile[0].maxVertical
                                ) :
                                prospect.phys_profile[0].maxVertical
                            }
                          </span>&nbsp;
                          {
                            typeof (prospect.phys_profile[0].maxVertical) === "number" && (
                              <span>{isHE ? "אינצ'ים" : 'inches'}</span>

                            )

                          }

                        </li>
                      </ul>
                    </div>

                    <div className={isHE ? "text-right" : "text-left"}>
                      <h3
                        className={
                          isHE ? "font-varelaRound text-xl font-bold" : "font-staatliches text-xl font-semibold"
                        }
                        style={{ color: "#7A4B4F" }}
                      >
                        <GrNotes />
                        {isHE ? 'הערות על הפרופיל הפיזי' : 'Notes on Physical Profile'}
                      </h3>
                      <p className="text-sm mb-3">
                        {isHE ? 'קליק על + להרחבה' : '(click + to expand)'}
                      </p>
                      {Array.isArray(prospect.phys_profile[0].scoutingNotes) &&
                        prospect.phys_profile[0].scoutingNotes.length > 0 ? (
                        <ScoutingNotes
                          notes={prospect.phys_profile[0].scoutingNotes?.filter((_: any, idx: number) =>
                            isHE ? idx % 2 !== 0 : idx % 2 === 0
                          )}
                          isHE={isHE}
                        />
                      ) : (
                        <p>{isHE ? 'אין הערות על הפרופיל הפיזי' : 'No notes on physical profile available'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={isHE ? "text-right mt-3" : "text-left mt-3"}>
                <h3
                  className={
                    isHE ? "font-varelaRound text-xl font-bold" : "font-staatliches text-xl font-semibold"
                  }
                  style={{ color: "#7A4B4F" }}
                >
                  <span>
                    {isHE ? 'רשמי סקאוטינג' : 'Scouting Notes'}&nbsp;
                  </span>
                  <span>
                    {isHE ? '(קליק על + להרחבה)' : '(click + to expand)'}
                  </span>
                </h3>

                {/* Offense and Defense Row */}
                <div className="grid grid-cols-1 gap-4 mt-5 md:grid-cols-2">
                  <div>
                    <h4
                      className={
                        isHE
                          ? "font-varelaRound text-lg font-bold"
                          : "font-staatliches text-lg font-semibold"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <GiCrossbow /> {isHE ? 'התקפה' : 'Offense'}
                    </h4>
                    <p style={{ color: "#222121", fontSize: "1rem" }}>
                      {isHE
                        ? `תפקיד התקפי: ${prospect.pos_and_roles[1]?.offensiveRole}`
                        : `Offensive Role: ${prospect.pos_and_roles[0]?.offensiveRole}`}
                    </p>
                    <div className="strengths mt-2">
                      <Tooltip
                        content={isCollapsed.offenseStrengths
                          ? isHE ? "הרחב" : "expand"
                          : isHE ? "צמצם" : "collapse"
                        }
                        position="bottom"
                      >
                        <button
                          className="collapse-btn"
                          onClick={() => toggleCollapse("offenseStrengths")}
                        >
                          {
                            isCollapsed.offenseStrengths ?
                              <AiOutlinePlus /> : <AiOutlineMinus />
                          }
                        </button>
                      </Tooltip>
                      <span className='green-text ml-0.5 text-base font-semibold'>
                        {isHE ? 'חוזקות' : 'Strengths'}
                      </span>
                      {!isCollapsed.offenseStrengths && (
                        <ScoutingNotes
                          notes={prospect.offensive_pros?.filter((_: any, idx: number) =>
                            isHE ? idx % 2 !== 0 : idx % 2 === 0
                          )}
                          isHE={isHE}
                        />
                      )}
                    </div>
                    <div className="areas-of-improvement mt-2">
                      <Tooltip
                        content={isCollapsed.offenseWeaknesses
                          ? isHE ? "הרחב" : "expand"
                          : isHE ? "צמצם" : "collapse"
                        }
                        position="bottom"
                      >
                        <button
                          className="collapse-btn"
                          onClick={() => toggleCollapse("offenseWeaknesses")}
                        >
                          {
                            isCollapsed.offenseWeaknesses ?
                              <AiOutlinePlus /> : <AiOutlineMinus />
                          }
                        </button>
                      </Tooltip>
                      <span className='red-text ml-0.5 text-base font-semibold'>
                        {isHE ? 'נקודות לשיפור' : 'Areas of Improvement'}
                      </span>
                      {!isCollapsed.offenseWeaknesses && (
                        <ScoutingNotes notes={prospect.offensive_cons?.filter((_: any, idx: number) =>
                          isHE ? idx % 2 !== 0 : idx % 2 === 0
                        ) || []} isHE={isHE} />
                      )}
                    </div>
                  </div>
                  <div className='mt-3 md:mt-0'>
                    <h4
                      className={
                        isHE
                          ? "font-varelaRound text-lg font-bold"
                          : "font-staatliches text-lg font-semibold"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <GiBlackHandShield /> {isHE ? 'הגנה' : 'Defense'}
                    </h4>
                    <p style={{ color: "#222121", fontSize: "1rem" }}>
                      {isHE
                        ? `תפקיד הגנתי: ${prospect.pos_and_roles[1]?.defensiveRole}`
                        : `Defensive Role: ${prospect.pos_and_roles[0]?.defensiveRole}`}
                    </p>
                    <div className="strengths mt-2">
                      <Tooltip
                        content={isCollapsed.defenseStrengths
                          ? isHE ? "הרחב" : "expand"
                          : isHE ? "צמצם" : "collapse"
                        }
                        position="bottom"
                      >
                        <button
                          className="collapse-btn"
                          onClick={() => toggleCollapse("defenseStrengths")}
                        >
                          {
                            isCollapsed.defenseStrengths ?
                              <AiOutlinePlus /> : <AiOutlineMinus />
                          }
                        </button>
                      </Tooltip>
                      <span className='green-text ml-0.5 text-base font-semibold'>
                        {isHE ? 'חוזקות' : 'Strengths'}
                      </span>
                      {!isCollapsed.defenseStrengths && (
                        <ScoutingNotes
                          notes={prospect.defensive_pros?.filter((_: any, idx: number) =>
                            isHE ? idx % 2 !== 0 : idx % 2 === 0
                          ) || []}
                          isHE={isHE}
                        />
                      )}
                    </div>
                    <div className="areas-of-improvement mt-2">
                      <Tooltip
                        content={isCollapsed.defenseWeaknesses
                          ? isHE ? "הרחב" : "expand"
                          : isHE ? "צמצם" : "collapse"
                        }
                        position="bottom"
                      >
                        <button
                          className="collapse-btn"
                          onClick={() => toggleCollapse("defenseWeaknesses")}
                        >
                          {
                            isCollapsed.defenseWeaknesses ?
                              <AiOutlinePlus /> : <AiOutlineMinus />
                          }
                        </button>
                      </Tooltip>
                      <span className='red-text ml-0.5 text-base font-semibold'>
                        {isHE ? 'נקודות לשיפור' : 'Areas of Improvement'}
                      </span>
                      {!isCollapsed.defenseWeaknesses && (
                        <ScoutingNotes notes={prospect.defensive_cons?.filter((_: any, idx: number) =>
                          isHE ? idx % 2 !== 0 : idx % 2 === 0
                        ) || []} isHE={isHE} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Intangibles and Personality Row */}
                <div className="grid grid-cols-1 gap-4 mt-8 md:grid-cols-2">

                  <div>
                    <h4
                      className={
                        isHE
                          ? "font-varelaRound text-lg font-bold mb-1"
                          : "font-staatliches text-lg font-semibold mb-1"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <MdOutlineSportsHandball />
                      {isHE ? "אינטנג'בלס" : 'Intangibles'}
                    </h4>
                    <ScoutingNotes
                      notes={prospect.intangibles?.filter((_: any, idx: number) =>
                        isHE ? idx % 2 !== 0 : idx % 2 === 0
                      ) || []}
                      isHE={isHE}
                    />
                  </div>
                  <div>
                    <h4
                      className={
                        isHE
                          ? "font-varelaRound text-lg font-bold mb-1"
                          : "font-staatliches text-lg font-semibold mb-1"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <MdOutlinePsychologyAlt />
                      {isHE ? 'רקע והתנהגות מחוץ למגרש' : 'Background & Off-Court Demeanour'}
                    </h4>
                    <ScoutingNotes
                      notes={prospect.personality?.filter((_: any, idx: number) =>
                        isHE ? idx % 2 !== 0 : idx % 2 === 0
                      ) || []}
                      isHE={isHE}
                    />
                  </div>
                </div>

                {prospect.pro_comps && prospect.pro_comps.length > 0 && (
                  <div className="mt-4">
                    <h4
                      className={
                        isHE
                          ? "font-varelaRound text-lg font-bold"
                          : "font-staatliches text-lg font-semibold"
                      }
                      style={{ color: "#7A4B4F" }}
                    >
                      <BsProjector />
                      {isHE ? 'צפי מקצועי-השוואתי' : 'Pro Comp Projection'}
                    </h4>
                    <div className="mt-1 text-pretty">
                      {isHE && prospect.pro_comps ? (
                        <>
                          {prospect.pro_comps[1]}{' '}
                        </>
                      ) : (
                        <>
                          {prospect.pro_comps[0]}{' '}
                        </>
                      )
                      }
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      ) : (
        <p>Loading prospect data...</p>
      )}
    </div>

  );
};

export default ProspectPage;